/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, Paper } from "@mui/material";
import { useMemo, useState } from "react";
import PageContainer from "../page-container/page-container";
import { useNotification } from "../../context/notification-provider";
import LoadingScreen from "../progress/loading-screen";
import { ScoringRule } from "./types";
import useScoringRules from "./use-scoring-rules";
import getColumns from "./data-grid";
import ExportToolbar, {
  ExportToolbarProps,
} from "../grid-toolbar/export-toolbar";
import EditScoringRuleDialog from "./edit-dialog";
import ScoringRuleFilterBar from "./filters/scoring-rule-filter-bar";
import {
  EMPTY_SCORING_RULE_FILTERS,
  filterScoringRules,
  getRuleTypes,
} from "./filters/scoring-rule-filters";

const ScoringRules = () => {
  const { showNotification } = useNotification();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<ScoringRule | null>(null);

  const [filters, setFilters] = useState(EMPTY_SCORING_RULE_FILTERS);

  const { data: scoringRules, isLoading, error, mutate } = useScoringRules();

  const allRules = useMemo(() => scoringRules ?? [], [scoringRules]);
  const ruleTypes = useMemo(() => getRuleTypes(allRules), [allRules]);
  const filteredRules = useMemo(
    () => filterScoringRules(allRules, filters, (rule) => rule),
    [allRules, filters],
  );

  const handleOnRefreshClick = React.useCallback(() => {
    showNotification({
      message: `Refreshing scoring rules data...`,
      severity: "info",
      duration: 2000,
    });
    mutate();
  }, [showNotification, mutate]);

  const handleOnEdit = React.useCallback((rule: ScoringRule) => {
    setRuleToEdit(rule);
    setIsEditDialogOpen(true);
  }, []);

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setRuleToEdit(null);
    mutate();
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading scoring rules data.
      </Alert>
    );
  }

  return (
    <PageContainer
      title="Scoring Rules"
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleOnRefreshClick}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
        </Stack>
      }
    >
      <Box
        sx={{
          flex: 1,
          width: "100%",
          height: "100%",
        }}
      >
        <ScoringRuleFilterBar
          filters={filters}
          onChange={setFilters}
          ruleTypes={ruleTypes}
          shownCount={filteredRules.length}
          totalCount={allRules.length}
        />
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading scoring rules data.
          </Alert>
        ) : (
          <Paper
            elevation={2}
            sx={{
              height: "650px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <DataGrid
              slots={{ toolbar: ExportToolbar }}
              slotProps={{
                toolbar: { fileName: "scoring-rules" } as ExportToolbarProps,
              }}
              showToolbar
              rows={filteredRules}
              columns={getColumns({
                onEdit: handleOnEdit,
              })}
              disableRowSelectionOnClick
              loading={isLoading}
              sx={{
                height: "560px",
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                  outline: "transparent",
                },
                [`& .${gridClasses.columnHeader}`]: {
                  backgroundColor: "#f5f6fa",
                  outline: "transparent",
                },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.row}:hover`]: {
                  cursor: "pointer",
                },
              }}
            />
          </Paper>
        )}
      </Box>
      {isEditDialogOpen && (
        <EditScoringRuleDialog
          open={isEditDialogOpen}
          scoringRule={ruleToEdit}
          onClose={handleEditDialogClose}
        />
      )}
    </PageContainer>
  );
};

export default ScoringRules;
